
import React from 'react';
import { TrackDownload } from '@/lib/types';
import TrackPreview from './TrackPreview';
import SpotifyUrlInput from './upload/SpotifyUrlInput';
import FileUploader from './upload/FileUploader';
import UploadButton from './upload/UploadButton';
import StepCard from './upload/StepCard';
import TrackDetailsInput from './upload/TrackDetailsInput';
import { useTrackUpload } from '@/hooks/track-upload';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface UploadFormProps {
  onUploadSuccess: (upload: TrackDownload) => void;
  artistId: string | null;
  onSuccess: (upload?: TrackDownload) => void;
}

const UploadForm: React.FC<UploadFormProps> = ({ onUploadSuccess, artistId, onSuccess }) => {
  const {
    spotifyUrl,
    isUrlValid,
    selectedFile,
    isUploading,
    trackPreview,
    trackTitle,
    trackDescription,
    uploadError,
    handleSpotifyUrlChange,
    handleFileChange,
    handleUpload,
    setTrackPreview,
    setTrackTitle,
    setTrackDescription,
  } = useTrackUpload(artistId, onUploadSuccess);

  const processUpload = async (e: React.FormEvent) => {
    const result = await handleUpload(e);
    if (result.success) {
      // Pass the upload data to the success handler
      onSuccess(result.upload); // Pass the upload to the onSuccess callback
    }
  };

  return (
    <form onSubmit={processUpload} className="space-y-5">
      {uploadError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      )}
      
      <StepCard>
        <SpotifyUrlInput 
          spotifyUrl={spotifyUrl}
          isUrlValid={isUrlValid}
          onChange={handleSpotifyUrlChange}
        />
        
        {trackPreview && (
          <TrackPreview 
            track={trackPreview} 
            onClose={() => setTrackPreview(null)} 
          />
        )}
      </StepCard>
      
      <StepCard>
        <TrackDetailsInput
          title={trackTitle}
          description={trackDescription}
          onTitleChange={setTrackTitle}
          onDescriptionChange={setTrackDescription}
        />
      </StepCard>
      
      <StepCard className="py-3">
        <FileUploader 
          selectedFile={selectedFile}
          onChange={handleFileChange}
        />
      </StepCard>
      
      <UploadButton 
        isDisabled={!isUrlValid || !spotifyUrl || !selectedFile || isUploading || !artistId || !trackTitle}
        isUploading={isUploading}
      />
    </form>
  );
};

export default UploadForm;
